import { Button, Checkbox, Divider, Popover, Tooltip } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { IoFilterSharp } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import { AiOutlineCheck } from 'react-icons/ai';
import { FilterQueryItems } from '../types';

import * as S from './styles';

interface QuerySelectFiltersProps {
  queryFilters?: {
    filters: FilterQueryItems[];
    clearSearchParams: (paramsToDelete?: string[]) => void;
    itemsToMap: FilterQueryItems[] | undefined;
    setItemsToMap: React.Dispatch<
      React.SetStateAction<FilterQueryItems[] | undefined>
    >;
  };
  handleUpdateFilters: any;
  selectedFilters?: string[];
}

const QuerySelectFilters = ({
  queryFilters,
  handleUpdateFilters,
  selectedFilters,
}: QuerySelectFiltersProps) => {
  const { t } = useTranslation('dashboard');
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisible = useCallback(
    () => setIsVisible(crrVisible => !crrVisible),
    [],
  );

  const defaultFilters = useMemo(
    () => selectedFilters || [],
    [selectedFilters],
  );

  const optionsWithPermissions = useMemo(() => {
    return queryFilters?.filters?.filter(item => item.visible);
  }, [queryFilters]);

  const handleChange = useCallback(
    (value: string, add: boolean) => {
      const findItem = optionsWithPermissions?.find(
        filterItem => filterItem.value === value,
      );

      if (add && findItem) {
        queryFilters?.setItemsToMap(previous =>
          previous ? [...previous, findItem] : [findItem],
        );
        handleUpdateFilters([...defaultFilters, findItem.value]);
      } else {
        const removeItem = queryFilters?.itemsToMap?.filter(
          filterItem => filterItem.value !== value,
        );
        const removePrefrencesItem = defaultFilters.filter(
          item => item !== value,
        );

        handleUpdateFilters(removePrefrencesItem);
        queryFilters?.clearSearchParams([value]);
        queryFilters?.setItemsToMap(removeItem);
      }
    },
    [defaultFilters, handleUpdateFilters, optionsWithPermissions, queryFilters],
  );

  const options = useMemo(() => {
    const grouped = optionsWithPermissions?.reduce((acc: any, item: any) => {
      if (!acc[item.category]) {
        acc[item.category] = [];
      }
      acc[item.category].push(item);
      return acc;
    }, {});
    return (
      grouped &&
      Object.keys(grouped).map(key => {
        return {
          group: key,
          items: grouped[key]
            .sort((a: FilterQueryItems, b: FilterQueryItems) =>
              a.name.localeCompare(b.name),
            )
            .map((item: FilterQueryItems) => {
              return { label: item?.name, value: item?.value };
            }),
        };
      })
    );
  }, [optionsWithPermissions]);

  const handleSelectAll = useCallback(() => {
    const order = [
      'msisdn',
      'iccid',
      'imsi',
      'networkBlockStatus',
      'online',
      'status',
      'operator',
      'connectedOperator',
      'lastConnectionFrom',
      'customerName',
      'automaticBlocking',
      'technology',
      'apn',
      'activationDateFrom',
      'franchise',
      'nickname',
      'imei',
      'imeiLocked',
    ];

    const customSort = (a: FilterQueryItems, b: FilterQueryItems) => {
      const indexA = order.indexOf(a.value);
      const indexB = order.indexOf(b.value);

      if (indexA === -1 && indexB === -1) {
        return 0;
      }
      if (indexA === -1) {
        return 1;
      }
      if (indexB === -1) {
        return -1;
      }

      return indexA - indexB;
    };

    const sortedArray = optionsWithPermissions?.sort(customSort);
    const itemsToSaveOnLocalStorage = sortedArray?.map(item => item.value);
    queryFilters?.clearSearchParams();
    queryFilters?.setItemsToMap(sortedArray);
    handleUpdateFilters(itemsToSaveOnLocalStorage);
  }, [handleUpdateFilters, optionsWithPermissions, queryFilters]);

  const content = useMemo(
    () => (
      <S.QuerySelectFiltersContainer>
        <div className="query-select-filters-header">
          <span>{t('query-builder.title')}</span>
          <Button onClick={handleSelectAll} icon={<AiOutlineCheck />}>
            {t('query-builder.select-all')}
          </Button>
        </div>
        <div className="query-select-filters-options-wrapper">
          {options?.map((option: any) => (
            <div
              key={option?.group}
              className="query-select-filters-options-container"
            >
              <Divider orientation="left">
                {t(`query-builder.options-title.${option.group}`)}
              </Divider>
              <div className="query-select-filters-options-content">
                {option?.items.map(
                  (itemOption: { label: string; value: string }) => (
                    <Checkbox
                      key={itemOption.label}
                      value={itemOption.value}
                      checked={Boolean(
                        defaultFilters?.find(item => item === itemOption.value),
                      )}
                      onChange={e => {
                        handleChange(itemOption?.value || '', e.target.checked);
                      }}
                    >
                      {itemOption.label}
                    </Checkbox>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      </S.QuerySelectFiltersContainer>
    ),
    [defaultFilters, handleChange, handleSelectAll, options, t],
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={isVisible}
      onOpenChange={toggleVisible}
      placement="topLeft"
    >
      <Tooltip title={t('see-options')}>
        <Button
          style={{
            padding: '0 8px',
          }}
        >
          <IoFilterSharp style={{ marginTop: 6 }} />
        </Button>
      </Tooltip>
    </Popover>
  );
};

export default QuerySelectFilters;
